import { useEffect, useState } from "react";
import {
  frontendApi,
  guestApi,
  withFallback,
  isTransportError,
  USE_FIXTURES,
} from "@/lib/api";
import {
  PROPERTIES,
  COLLECTIONS,
  TESTIMONIALS,
  AMENITY_OPTIONS,
} from "@/data/properties";
import {
  BOOKINGS,
  CONVERSATIONS,
  REVIEWS_SUBMITTED,
  TESTIMONIALS_SUBMITTED,
  findConversation,
} from "@/data/accountFixtures";
import { HOST_PLANS } from "@/data/staticContent";

const fixture = (value, emptyValue) => (USE_FIXTURES ? value : emptyValue);
const fixtureArray = (value, fixtureValue) => {
  if (Array.isArray(value) && value.length > 0) return value;
  return USE_FIXTURES ? fixtureValue : [];
};

export const normalizeProperty = (value) => {
  if (!value) return null;
  const imageObjects = Array.isArray(value.images) ? value.images : [];
  const images = imageObjects
    .map((image) => (typeof image === "string" ? image : image?.url))
    .filter(Boolean);
  if (!images.length && value.cover_image?.url) images.push(value.cover_image.url);

  const features = Array.isArray(value.features) ? value.features : [];
  const amenities = features
    .map((feature) => (typeof feature === "string" ? feature : feature?.name))
    .filter(Boolean);
  const host = value.host || value.admin || {};
  const slug = value.slug || value.id;
  const price = Number(value.price_per_night ?? value.price ?? 0);
  const rating = Number(value.average_rating ?? value.rating ?? 0);
  const reviewCount = Number(value.review_count ?? value.reviews_count ?? 0);
  const maxGuests = Number(value.max_guests ?? value.guests ?? 1);
  const beds = Number(value.beds ?? value.bedrooms ?? 0);

  return {
    ...value,
    numericId: value.id,
    id: slug,
    slug,
    name: value.title || value.name || "StayEasy property",
    title: value.title || value.name || "StayEasy property",
    location: typeof value.location === "string" ? value.location : "",
    price,
    price_per_night: price,
    rating,
    average_rating: rating,
    reviews: reviewCount,
    review_count: reviewCount,
    guests: maxGuests,
    max_guests: maxGuests,
    bedrooms: beds,
    beds,
    baths: Number(value.baths ?? 0),
    images,
    amenities,
    features,
    host: {
      ...host,
      name: host.name || "StayEasy host",
      slug: host.slug || host.public_slug || "",
      avatar: host.avatar || host.avatar_url || "",
      years: Number(host.years ?? host.hosting_years ?? 0),
    },
  };
};

const dateIsPast = (value) => {
  if (!value) return false;
  const d = new Date(`${value}T23:59:59`);
  return d.getTime() < Date.now();
};

export const normalizeBooking = (value) => {
  if (!value) return null;
  const property = normalizeProperty(value.property);
  const checkIn = value.check_in ?? value.checkIn;
  const checkOut = value.check_out ?? value.checkOut;
  const rawStatus = value.status;
  const status = rawStatus === "confirmed" && dateIsPast(checkOut) ? "completed" : rawStatus;
  return {
    ...value,
    status,
    rawStatus,
    reference: value.reference || value.id,
    checkIn,
    checkOut,
    guests: Number(value.guests ?? 1),
    nights: Number(value.nights ?? 0),
    total: Number(value.total_amount ?? value.total ?? 0),
    currency: value.currency || "USD",
    property,
    unreadMessages: Number(value.unread_chat_count ?? value.unreadMessages ?? 0),
    invoiceReady: Boolean(value.transaction?.id || value.transaction?.invoice_number),
    canReview: rawStatus === "confirmed" && dateIsPast(checkOut),
  };
};

const formatStayDates = (booking) => {
  if (!booking?.checkIn || !booking?.checkOut) return "";
  const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(booking.checkIn)} – ${fmt(booking.checkOut)}`;
};

const threadIsOpen = (booking) => {
  if (!booking || booking.rawStatus !== "confirmed" || !booking.checkOut) return false;
  const closes = new Date(`${booking.checkOut}T23:59:59`);
  closes.setDate(closes.getDate() + 1);
  return closes.getTime() >= Date.now();
};

export const normalizeConversation = (value) => {
  const booking = normalizeBooking(value);
  if (!booking) return null;
  const latest = value.latest_message || value.latestMessage;
  return {
    ...booking,
    bookingId: booking.id,
    property: booking.property,
    host: booking.property?.host || {},
    stayDates: formatStayDates(booking),
    lastMessage: latest?.message || "No messages yet.",
    lastTimestamp: latest?.created_at
      ? new Date(latest.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
      : "",
    unread: booking.unreadMessages,
    open: threadIsOpen(booking),
  };
};

export const normalizeThread = (value) => {
  if (!value) return null;
  const booking = normalizeBooking(value.booking);
  if (!booking) return null;
  const messages = (value.messages || []).map((message) => ({
    ...message,
    from: message.sender_type === "customer" ? "guest" : "host",
    body: message.message,
    at: message.created_at
      ? new Date(message.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
      : "",
  }));
  return {
    booking,
    property: booking.property,
    host: booking.property?.host || {},
    stayDates: formatStayDates(booking),
    open: threadIsOpen(booking),
    messages,
  };
};

const normalizePlan = (plan) => ({
  ...plan,
  id: plan.id || plan.key,
  key: plan.key || plan.id,
  price: Number(plan.monthly_price ?? plan.price ?? 0),
  featured: Boolean(plan.recommended ?? plan.featured),
  tagline: plan.tagline || (Number(plan.monthly_price ?? plan.price ?? 0) === 0 ? "Start hosting with the essentials" : "Monthly host subscription"),
  cta: Number(plan.monthly_price ?? plan.price ?? 0) === 0 ? "Start free" : "Choose plan",
});

const useApiResource = (fn, fallback, deps = []) => {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const value = await withFallback(fn, fallback);
        if (!cancelled) setData(value ?? fallback);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setData(fallback);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
};

export const useHome = () => {
  const fallback = { properties: fixture(PROPERTIES, []), collections: fixture(COLLECTIONS, []), testimonials: fixture(TESTIMONIALS, []), amenities: fixture(AMENITY_OPTIONS, []) };
  const { data, loading, error } = useApiResource(() => frontendApi.home(), fallback, []);
  return {
    properties: fixtureArray(data?.properties, PROPERTIES),
    collections: fixtureArray(data?.collections, COLLECTIONS),
    testimonials: fixtureArray(data?.testimonials, TESTIMONIALS),
    amenities: fixtureArray(data?.amenities, AMENITY_OPTIONS),
    loading, error,
  };
};

export const useProperties = (params) => {
  const key = JSON.stringify(params || {});
  const fallback = fixture(PROPERTIES, []);
  const [items, setItems] = useState(fallback);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await frontendApi.properties(params);
        if (!cancelled) {
          setItems(fixtureArray((res.unwrapped || []).map(normalizeProperty), PROPERTIES));
          setMeta(res.meta || null);
        }
      } catch (err) {
        if (USE_FIXTURES && isTransportError(err)) {
          if (!cancelled) setItems(PROPERTIES);
        } else if (!cancelled) {
          setItems([]);
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { items, meta, loading, error };
};

export const useProperty = (slug) => {
  const [property, setProperty] = useState(() => normalizeProperty(USE_FIXTURES ? PROPERTIES.find((item) => item.id === slug) || null : null));
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    (async () => {
      try {
        const res = await frontendApi.property(slug);
        if (!cancelled) setProperty(normalizeProperty(res.unwrapped || (USE_FIXTURES ? PROPERTIES.find((item) => item.id === slug) || null : null)));
      } catch (err) {
        if (USE_FIXTURES && isTransportError(err)) {
          if (!cancelled) setProperty(normalizeProperty(PROPERTIES.find((item) => item.id === slug) || null));
        } else if (err?.response?.status === 404) {
          if (!cancelled) { setNotFound(true); setProperty(null); }
        } else if (!cancelled) {
          setProperty(null); setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);
  return { property, loading, notFound, error };
};

export const useHostPlans = () => {
  const fallback = fixture(HOST_PLANS, []);
  const { data, loading, error } = useApiResource(() => frontendApi.hostPlans(), fallback, []);
  const rawPlans = Array.isArray(data) ? data : data?.plans || [];
  return { plans: fixtureArray(rawPlans.map(normalizePlan), HOST_PLANS), loading, error };
};

export const useMyBookings = () => {
  const fallback = fixture(BOOKINGS, []);
  const { data, error, loading } = useApiResource(() => guestApi.bookings(), fallback, []);
  const normalized = Array.isArray(data) ? data.map(normalizeBooking).filter(Boolean) : [];
  return { items: fixtureArray(normalized, BOOKINGS), error, loading };
};

export const useConversations = () => {
  const fallback = fixture(CONVERSATIONS, []);
  const { data, error, loading } = useApiResource(() => guestApi.conversations(), fallback, []);
  const normalized = Array.isArray(data) ? data.map(normalizeConversation).filter(Boolean) : [];
  return { items: fixtureArray(normalized, CONVERSATIONS), error, loading };
};

export const useConversation = (bookingId) => {
  const [thread, setThread] = useState(() => USE_FIXTURES ? findConversation(bookingId) : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await guestApi.bookingMessages(bookingId);
        if (!cancelled) setThread(normalizeThread(res.unwrapped) || (USE_FIXTURES ? findConversation(bookingId) || null : null));
      } catch (err) {
        if (USE_FIXTURES && isTransportError(err)) {
          if (!cancelled) setThread(findConversation(bookingId) || null);
        } else if (!cancelled) {
          setThread(null); setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);
  return { thread, setThread, loading, error };
};

export const useMyReviews = () => {
  const [reviews, setReviews] = useState(() => fixture(REVIEWS_SUBMITTED, []));
  const [testis, setTestis] = useState(() => fixture(TESTIMONIALS_SUBMITTED, []));
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await withFallback(() => guestApi.reviews(), { eligible_bookings: [], property_reviews: [], testimonials: [] });
        if (!cancelled) {
          setEligible((data?.eligible_bookings || []).map(normalizeBooking).filter(Boolean));
          setReviews((data?.property_reviews || []).map((review) => ({ ...review, property: normalizeProperty(review.property) })));
          setTestis(data?.testimonials || []);
        }
      } catch (err) {
        if (!cancelled) {
          setEligible(USE_FIXTURES ? BOOKINGS.filter((b) => b.canReview) : []);
          setReviews(fixture(REVIEWS_SUBMITTED, []));
          setTestis(fixture(TESTIMONIALS_SUBMITTED, []));
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { eligible, reviews, setReviews, testis, setTestis, loading, error };
};
